import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';

import Button from '../Button';
import ModalBody from './ModalBody';
import * as styles from './Modal.scss';

/**
 * @class Modal
 * @classdesc Reusable Modal Component
 * @example ./Modal.md
 */
export default class Modal extends Component {
  static propTypes = {
    onClose: PropTypes.func.isRequired,
    allowClickOutside: PropTypes.bool,
    buttons: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        click: PropTypes.func,
        svg: PropTypes.node,
        svgLeft: PropTypes.bool,
        svgRight: PropTypes.bool,
        type: PropTypes.string,
        className: PropTypes.string,
        style: PropTypes.objectOf(
          PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
          ]),
        ),
        disabled: PropTypes.bool,
      }),
    ),
    className: PropTypes.string,
    contentClassName: PropTypes.string,
    contentStyle: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    ),
    contentWrapperClassName: PropTypes.string,
    contentWrapperStyle: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    ),
    disableClickOutside: PropTypes.bool,
    loadingClassName: PropTypes.string,
    loadingMessage: PropTypes.string,
    modalContentLoading: PropTypes.bool,
    open: PropTypes.bool,
    overlayClassName: PropTypes.string,
    overlayStyle: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    ),
    showCloseButton: PropTypes.bool,
    style: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    ),
    subtitle: PropTypes.string,
    title: PropTypes.string,
  };

  static defaultProps = {
    allowClickOutside: true,
    buttons: [],
    className: null,
    contentClassName: null,
    contentStyle: null,
    contentWrapperClassName: null,
    contentWrapperStyle: null,
    disableClickOutside: false,
    loadingClassName: null,
    loadingMessage: null,
    modalContentLoading: false,
    open: true,
    overlayClassName: null,
    overlayStyle: null,
    showCloseButton: true,
    style: null,
    subtitle: '',
    title: '',
  };

  componentDidMount() {
    window.addEventListener('keydown', this.listenKeyboard);
  }

  componentWillUnmount() {
    const { onClose } = this.props;

    window.removeEventListener('keydown', this.listenKeyboard);
    return onClose();
  }

  handleClickOutside = () => {
    const { allowClickOutside, onClose } = this.props;

    if (allowClickOutside) return onClose();
    return null;
  };

  /**
   * @method ListenKeyboard
   * @param {Object} e - Event Object
   * @return {Function} onClose
   */
  listenKeyboard = e => {
    const { onClose } = this.props;

    if (e.key === 'Escape' || e.keyCode === 27) return onClose();
    return null;
  };

  /**
   * @method RenderBtns
   * @param {Array.<Object>} buttons - Button Payload
   * @return {Array.<ReactElement>} - Array of Button Components
   */
  renderBtns = buttons => {
    return buttons.map(button => {
      return (
        <Button
          key={button.text}
          type={button.type}
          text={button.text}
          click={button.click}
          svg={button.svg}
          svgLeft={button.svgLeft}
          svgRight={button.svgRight}
          className={button.className}
          style={button.style}
          disabled={button.disabled}
        />
      );
    });
  };

  render() {
    const {
      buttons,
      children,
      className,
      contentClassName,
      contentStyle,
      contentWrapperClassName,
      contentWrapperStyle,
      disableClickOutside,
      loadingClassName,
      loadingMessage,
      modalContentLoading,
      open,
      overlayClassName,
      overlayStyle,
      showCloseButton,
      style,
      subtitle,
      title,
    } = this.props;

    if (!open) return null;
    return (
      <div className={classNames(styles.modalWrapper, className)} style={style}>
        <div className={classNames(styles.modalOverlay, overlayClassName)} style={overlayStyle} />
        <div className={classNames(styles.modalContentWrapper, contentWrapperClassName)} style={contentWrapperStyle}>
          {!children &&
            <ModalBody
              className={contentClassName}
              clickOutsideFn={this.handleClickOutside}
              disableClickOutside={disableClickOutside}
              loading={modalContentLoading}
              loadingClassName={loadingClassName}
              loadingMessage={loadingMessage}
              showCloseButton={showCloseButton}
              style={contentStyle}
            >
              <h3 className={styles.title}>{title}</h3>
              <p className={styles.subtitle}>{parse(subtitle)}</p>
              <div className={styles.buttonsContainer}>
                {this.renderBtns(buttons)}
              </div>
            </ModalBody>}
          {children &&
            <ModalBody
              className={contentClassName}
              clickOutsideFn={this.handleClickOutside}
              disableClickOutside={disableClickOutside}
              loading={modalContentLoading}
              loadingClassName={loadingClassName}
              loadingMessage={loadingMessage}
              showCloseButton={showCloseButton}
              style={contentStyle}
            >
              {children}
            </ModalBody>}
        </div>
      </div>
    );
  }
}
